import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { surfaceRadiusClasses, surfaceVariantClasses } from '../atoms/Surface/Surface';
import { cn } from '../lib/cn';

/**
 * `size` sets `--card-spacing` (spacing 3, 4 or 6). Every slot reads it, so a
 * Card with only a `CardContent` is a box with the same inset on all sides.
 */
const cardVariants = cva(
  'group/card text-foreground flex flex-col gap-(--card-spacing) overflow-hidden py-(--card-spacing) has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0',
  {
    variants: {
      variant: {
        default: surfaceVariantClasses.default,
        elevated: surfaceVariantClasses.elevated,
        sunken: surfaceVariantClasses.sunken,
      },
      radius: surfaceRadiusClasses,
      size: {
        sm: '[--card-spacing:--spacing(3)]',
        md: '[--card-spacing:--spacing(4)]',
        lg: '[--card-spacing:--spacing(6)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      radius: '2xl',
      size: 'md',
    },
  },
);

type CardProps = React.ComponentProps<'div'> & VariantProps<typeof cardVariants>;

function Card({ className, variant, radius, size = 'md', ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      data-size={size}
      data-variant={variant ?? 'default'}
      className={cn(cardVariants({ variant, radius, size }), className)}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'group/card-header @container/card-header grid auto-rows-min items-start gap-1 px-(--card-spacing) has-data-[slot=card-action]:grid-cols-[minmax(0,1fr)_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-base/snug font-semibold wrap-anywhere group-data-[size=sm]/card:text-sm/snug',
        className,
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-muted-foreground text-sm wrap-anywhere', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}

const cardContentVariants = cva('', {
  variants: {
    inset: {
      default: 'px-(--card-spacing)',
      none: '',
    },
  },
  defaultVariants: {
    inset: 'default',
  },
});

/**
 * `inset="none"` drops the side inset, for content such as a `Table` that
 * runs edge to edge. Pair a `size="lg"` card with `Table inset="md"` so the
 * cells line up with the header.
 */
function CardContent({
  className,
  inset,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardContentVariants>) {
  return (
    <div
      data-slot="card-content"
      className={cn(cardContentVariants({ inset }), className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'border-border-subtle bg-muted/50 flex items-center border-t p-(--card-spacing)',
        className,
      )}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
